import { Injectable } from "@angular/core";
import { Observable, ReplaySubject } from "rxjs";
import { THEME_STORAGE_KEY } from "app/services/theme/theme.service";

@Injectable()
export class LocalStorageService {
    private storage = localStorage;
    private observableChanges: Record<string, ReplaySubject<any>> = {};
    private readonly preservedKeys: string[] = [THEME_STORAGE_KEY];

    constructor() {}

    private ensureSubject(key: string): ReplaySubject<any> {
        if (!this.observableChanges[key]) {
            this.observableChanges[key] = new ReplaySubject(1);
        }
        return this.observableChanges[key];
    }

    public setItem(key: string, data: any): any {
        this.storage.setItem(key, JSON.stringify(data));
        this.ensureSubject(key).next(data);

        // Keep legacy "data" envelope in sync so getItem() and data listeners stay consistent.
        if (key !== "data") {
            let allData: any = {};
            try {
                allData = JSON.parse(this.storage.getItem("data")) || {};
            } catch {
                allData = {};
            }
            allData[key] = data;
            this.storage.setItem("data", JSON.stringify(allData));
            this.ensureSubject("data").next(allData);
        }
    }

    public getChangesOf<T = any>(key: string): Observable<T> {
        return this.ensureSubject(key).asObservable();
    }

    public clear(): void {
        const preserved: Record<string, string> = {};
        this.preservedKeys.forEach((key: string) => {
            const value = this.storage.getItem(key);
            if (value !== null) {
                preserved[key] = value;
            }
        });
        this.storage.clear();
        Object.keys(preserved).forEach((key: string) =>
            this.storage.setItem(key, preserved[key]),
        );
        Object.values(this.observableChanges).forEach(
            (observable: ReplaySubject<any>) => observable.next(null),
        );
    }

    public getKey(key: string): any {
        try {
            const data = JSON.parse(this.storage.getItem(key));
            return data;
        } catch (err) {
            return undefined;
        }
    }

    public getItem(key: string): any {
        try {
            const directData = JSON.parse(this.storage.getItem(key));
            if (directData !== null && typeof directData !== "undefined") {
                return directData;
            }
        } catch (err) {}

        try {
            const allData = JSON.parse(this.storage.getItem("data")) || {};
            return allData[key];
        } catch (err) {
            return undefined;
        }
    }

    public setDataItem(key: string, value: any): void {
        const data = JSON.parse(this.storage.getItem("data")) || {};
        const keys = key.split(".");
        const lastKey = keys.pop();
        const nestedObject = keys.reduce((current, part) => {
            if (!current[part]) {
                current[part] = {};
            }
            return current[part];
        }, data);

        if (lastKey) {
            nestedObject[lastKey] = value;
        }
        this.setItem("data", data);
    }
}
